import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { parseISO } from 'date-fns';
import { formatInTimeZone, toZonedTime, fromZonedTime, getTimezoneOffset } from 'date-fns-tz';
import { settingsApi } from '../services/api';
import { useAuth } from './AuthContext';
import { asUtcIfNaive } from '../lib/serverDate';
import { useTranslation } from 'react-i18next';
import { monthYear as monthYearIn, useDateLocale, weekdayLetters as weekdayLettersIn, weekdayShortNames as weekdayShortIn } from '../lib/dateLocale';

const DateTimeContext = createContext(null);

// Helper to get browser timezone
const getBrowserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

export { DATE_FORMATS, TIME_FORMATS, formatsForLocale } from '../lib/localeFormats';

// Available week start options
export const WEEK_START_OPTIONS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 6, label: 'Saturday' },
];

export const DateTimeProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  // Month and day names, and Today/Tomorrow/Yesterday, in the app's language.
  const { t, i18n } = useTranslation();
  const language = i18n.language || 'en';
  const locale = useDateLocale(language);
  const browserTimezone = getBrowserTimezone();
  const [settings, setSettings] = useState({
    timezone: browserTimezone,
    dateFormat: 'MMM d, yyyy',
    timeFormat: '12h',
    weekStartsOn: 1, // 0=Sunday, 1=Monday (default)
    // The browser timezone the user clicked "Keep <stored>" on. The banner
    // stays silent only while the user is still in this zone; moving to a
    // different zone triggers a re-prompt because the check is exact match.
    // Lives on the backend (not localStorage) so it syncs across devices.
    dismissedTimezone: null,
  });
  const [loading, setLoading] = useState(true);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [timezoneMismatch, setTimezoneMismatch] = useState(null);
  const [timezoneOffsetAdjust, setTimezoneOffsetAdjust] = useState(0);

  // Fetch user settings
  useEffect(() => {
    const fetchSettings = async () => {
      if (!isAuthenticated) {
        setLoading(false);
        setSettingsLoaded(true);
        return;
      }

      try {
        const response = await settingsApi.get();
        if (response.data) {
          let storedTimezone = response.data.timezone;

          // If timezone is a default value ("UTC" or old default "America/New_York") or not set,
          // prefer the browser timezone as it's likely more accurate
          // for users who never explicitly set their timezone
          const isDefaultTimezone = !storedTimezone || storedTimezone === 'UTC' || storedTimezone === 'America/New_York';
          if (isDefaultTimezone && browserTimezone !== 'UTC') {
            console.log(`[DateTimeContext] Stored timezone "${storedTimezone}" appears to be default. Saving browser timezone "${browserTimezone}" to settings.`);
            storedTimezone = browserTimezone;
            // Also save to backend so auto-scheduler uses the correct timezone
            try {
              await settingsApi.update({ timezone: browserTimezone });
            } catch (saveErr) {
              console.error('Failed to save timezone to settings:', saveErr);
            }
          }

          setSettings(prev => ({
            ...prev,
            timezone: storedTimezone || browserTimezone,
            dateFormat: response.data.date_format || prev.dateFormat,
            timeFormat: response.data.time_format || prev.timeFormat,
            weekStartsOn: response.data.week_starts_on ?? prev.weekStartsOn,
            dismissedTimezone: response.data.dismissed_timezone ?? null,
          }));
        }
      } catch (err) {
        console.error('Error fetching datetime settings:', err);
      } finally {
        setLoading(false);
        setSettingsLoaded(true);
      }
    };

    fetchSettings();
  }, [isAuthenticated, browserTimezone]);

  // Check timezone mismatch once after settings are loaded.
  // Silence applies only while the user is still in the exact zone they
  // dismissed — moving to any other zone makes dismissedTimezone stop matching
  // currentBrowserTz, so the banner re-prompts naturally.
  useEffect(() => {
    if (!settingsLoaded) return;
    const currentBrowserTz = getBrowserTimezone();
    if (currentBrowserTz !== settings.timezone && currentBrowserTz !== settings.dismissedTimezone) {
      setTimezoneMismatch({ stored: settings.timezone, detected: currentBrowserTz });
    }
  }, [settingsLoaded, settings.timezone, settings.dismissedTimezone]);

  // Re-check on tab focus / visibility change (e.g. after traveling with laptop)
  useEffect(() => {
    if (!settingsLoaded) return;
    const check = () => {
      const currentBrowserTz = getBrowserTimezone();
      if (currentBrowserTz !== settings.timezone && currentBrowserTz !== settings.dismissedTimezone) {
        setTimezoneMismatch({ stored: settings.timezone, detected: currentBrowserTz });
      }
    };
    const onVisibilityChange = () => { if (!document.hidden) check(); };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('focus', check);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('focus', check);
    };
  }, [settingsLoaded, settings.timezone, settings.dismissedTimezone]);

  // Update settings. Only fields explicitly present in newSettings get sent;
  // this lets callers PATCH a single field (e.g. dismissedTimezone: null)
  // without clobbering the others.
  const updateSettings = useCallback(async (newSettings) => {
    setSettings(prev => ({ ...prev, ...newSettings }));

    const apiKeys = {
      timezone: 'timezone',
      dateFormat: 'date_format',
      timeFormat: 'time_format',
      weekStartsOn: 'week_starts_on',
      dismissedTimezone: 'dismissed_timezone',
    };
    const payload = {};
    for (const [jsKey, apiKey] of Object.entries(apiKeys)) {
      if (jsKey in newSettings) payload[apiKey] = newSettings[jsKey];
    }
    if (Object.keys(payload).length === 0) return;

    try {
      await settingsApi.update(payload);
    } catch (err) {
      console.error('Error saving datetime settings:', err);
    }
  }, []);

  const acceptTimezoneChange = useCallback(async () => {
    if (!timezoneMismatch) return;
    const now = new Date();
    const oldOffsetMs = getTimezoneOffset(timezoneMismatch.stored, now);
    const newOffsetMs = getTimezoneOffset(timezoneMismatch.detected, now);
    const shiftHours = (newOffsetMs - oldOffsetMs) / (1000 * 60 * 60);
    setTimezoneOffsetAdjust(prev => prev + shiftHours);
    // Accepted: stored timezone now matches the device, so any prior dismissal
    // is stale — clear it so future moves to any zone re-prompt.
    await updateSettings({ timezone: timezoneMismatch.detected, dismissedTimezone: null });
    setTimezoneMismatch(null);
  }, [timezoneMismatch, updateSettings]);

  const dismissTimezoneMismatch = useCallback(() => {
    if (timezoneMismatch) {
      updateSettings({ dismissedTimezone: timezoneMismatch.detected });
    }
    setTimezoneMismatch(null);
  }, [timezoneMismatch, updateSettings]);

  // Format a date according to user settings
  const formatDate = useCallback((date, includeRelative = false) => {
    if (!date) return '';
    
    try {
      const dateObj = typeof date === 'string' ? parseISO(asUtcIfNaive(date)) : date;
      const zonedDate = toZonedTime(dateObj, settings.timezone);
      
      // Check for relative dates
      if (includeRelative) {
        const today = toZonedTime(new Date(), settings.timezone);
        today.setHours(0, 0, 0, 0);
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        
        const compareDate = new Date(zonedDate);
        compareDate.setHours(0, 0, 0, 0);
        
        if (compareDate.getTime() === today.getTime()) return t('common.today');
        if (compareDate.getTime() === tomorrow.getTime()) return t('common.tomorrow');
        if (compareDate.getTime() === yesterday.getTime()) return t('common.yesterday');
      }
      
      return formatInTimeZone(dateObj, settings.timezone, settings.dateFormat, { locale });
    } catch (err) {
      console.error('Error formatting date:', err);
      return String(date);
    }
  }, [settings.timezone, settings.dateFormat, locale, t]);

  // Format a time according to user settings
  const formatTime = useCallback((date) => {
    if (!date) return '';
    
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      const timeFormat = settings.timeFormat === '24h' ? 'HH:mm' : 'h:mm a';
      return formatInTimeZone(dateObj, settings.timezone, timeFormat, { locale });
    } catch (err) {
      console.error('Error formatting time:', err);
      return String(date);
    }
  }, [settings.timezone, settings.timeFormat, locale]);

  // Format date and time together
  const formatDateTime = useCallback((date) => {
    if (!date) return '';
    
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      const timeFormat = settings.timeFormat === '24h' ? 'HH:mm' : 'h:mm a';
      const fullFormat = `${settings.dateFormat}, ${timeFormat}`;
      return formatInTimeZone(dateObj, settings.timezone, fullFormat, { locale });
    } catch (err) {
      console.error('Error formatting datetime:', err);
      return String(date);
    }
  }, [settings.timezone, settings.dateFormat, settings.timeFormat, locale]);

  // Format just time with timezone consideration
  const formatTimeOnly = useCallback((date) => {
    return formatTime(date);
  }, [formatTime]);

  // Get day of week
  const formatDayOfWeek = useCallback((date, style = 'long') => {
    if (!date) return '';
    
    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      const formatStr = style === 'short' ? 'EEE' : 'EEEE';
      return formatInTimeZone(dateObj, settings.timezone, formatStr, { locale });
    } catch (err) {
      console.error('Error formatting day of week:', err);
      return '';
    }
  }, [settings.timezone, locale]);

  // Calendar headers in the app's language: one letter per weekday (Monday first
  // unless told otherwise), short day names, and "October 2026".
  const weekdayLetters = useCallback((weekStartsOn = 1) => weekdayLettersIn(language, weekStartsOn), [language]);
  const weekdayShortNames = useCallback((weekStartsOn = 1) => weekdayShortIn(language, weekStartsOn), [language]);
  const formatMonthYear = useCallback((date) => (date ? monthYearIn(date, language) : ''), [language]);

  // Convert to user's timezone
  const toUserTimezone = useCallback((date) => {
    if (!date) return null;

    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      return toZonedTime(dateObj, settings.timezone);
    } catch (err) {
      console.error('Error converting to user timezone:', err);
      return date;
    }
  }, [settings.timezone]);

  // Convert a date from user's timezone to UTC ISO string
  // Use this when saving dates to the backend
  const toUTCISO = useCallback((date) => {
    if (!date) return null;

    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      // fromZonedTime treats the input as if it's in the specified timezone
      // and returns the equivalent UTC time
      const utcDate = fromZonedTime(dateObj, settings.timezone);
      return utcDate.toISOString();
    } catch (err) {
      console.error('Error converting to UTC:', err);
      return date instanceof Date ? date.toISOString() : date;
    }
  }, [settings.timezone]);

  // Get hours and minutes in user's timezone from a Date object
  // Use this when reading times from FullCalendar events
  const getTimeInUserTimezone = useCallback((date) => {
    if (!date) return { hours: 0, minutes: 0 };

    try {
      const timeStr = formatInTimeZone(date, settings.timezone, 'HH:mm');
      const [hours, minutes] = timeStr.split(':').map(Number);
      return { hours, minutes };
    } catch (err) {
      console.error('Error getting time in user timezone:', err);
      return { hours: date.getHours(), minutes: date.getMinutes() };
    }
  }, [settings.timezone]);

  // Format a date as YYYY-MM-DD string using LOCAL date parts (not UTC)
  // Use this when saving due_date to the backend (date-only field, no timezone conversion)
  const toLocalDateString = useCallback((date) => {
    if (!date) return null;

    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      const year = dateObj.getFullYear();
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const day = String(dateObj.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch (err) {
      console.error('Error formatting local date string:', err);
      return null;
    }
  }, []);

  // Format a date as YYYY-MM-DD string in USER'S TIMEZONE
  // Use this when comparing dates against user settings like extra_work_days
  const toUserDateString = useCallback((date) => {
    if (!date) return null;

    try {
      const dateObj = typeof date === 'string' ? parseISO(date) : date;
      return formatInTimeZone(dateObj, settings.timezone, 'yyyy-MM-dd');
    } catch (err) {
      console.error('Error formatting user date string:', err);
      return null;
    }
  }, [settings.timezone]);

  const value = {
    settings,
    loading,
    settingsLoaded,
    updateSettings,
    formatDate,
    formatTime,
    formatDateTime,
    formatTimeOnly,
    formatDayOfWeek,
    weekdayLetters,
    weekdayShortNames,
    formatMonthYear,
    language,
    dateLocale: locale,
    toUserTimezone,
    toUTCISO,
    toLocalDateString,
    toUserDateString,
    getTimeInUserTimezone,
    timezone: settings.timezone,
    dateFormat: settings.dateFormat,
    timeFormat: settings.timeFormat,
    weekStartsOn: settings.weekStartsOn,
    browserTimezone,
    timezoneMismatch,
    acceptTimezoneChange,
    dismissTimezoneMismatch,
    timezoneOffsetAdjust,
  };

  return (
    <DateTimeContext.Provider value={value}>
      {children}
    </DateTimeContext.Provider>
  );
};

export const useDateTime = () => {
  const context = useContext(DateTimeContext);
  if (!context) {
    throw new Error('useDateTime must be used within a DateTimeProvider');
  }
  return context;
};

export default DateTimeContext;
